import React, { useCallback, useMemo } from "react";
import { AgGridReact } from "ag-grid-react";
import {
  ColDef,
  GridReadyEvent,
  ModuleRegistry,
  AllCommunityModule,
} from "ag-grid-community";
import { cn } from "../../lib/utils";

// Register all community modules once
ModuleRegistry.registerModules([AllCommunityModule]);

export interface AgGridTableProps {
  rowData: any[];
  columnDefs: ColDef[];
  loading?: boolean;
  height?: string | number;
  className?: string;
  onGridReady?: (params: GridReadyEvent) => void;
  quickFilterText?: string;
  pagination?: boolean;
  paginationPageSize?: number;
  domLayout?: "normal" | "autoHeight" | "print";
  rowHeight?: number;
  headerHeight?: number;
  emptyMessage?: string;
}

export const AgGridTable: React.FC<AgGridTableProps> = ({
  rowData,
  columnDefs,
  loading = false,
  height = "560px",
  className,
  onGridReady,
  quickFilterText,
  pagination = false,
  paginationPageSize = 10,
  domLayout = "autoHeight",
  rowHeight = 54,
  headerHeight = 48,
  emptyMessage = "No data found",
}) => {
  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: false,
      filter: false,
      resizable: false,
      suppressMovable: true,
      cellClass: "flex items-center text-[15px] text-slate-800 font-medium",
    }),
    []
  );

  const handleGridReady = useCallback(
    (params: GridReadyEvent) => {
      if (onGridReady) {
        onGridReady(params);
      }
    },
    [onGridReady]
  );

  const isEmpty = !loading && (!rowData || rowData.length === 0);
  const isAutoHeight = domLayout === "autoHeight";
  const isPlaceholder =
    isEmpty || (loading && (!rowData || rowData.length === 0));
  const effectiveDomLayout = isAutoHeight && isPlaceholder ? "normal" : domLayout;
  const effectiveHeight = isAutoHeight
    ? isPlaceholder
      ? "280px"
      : "auto"
    : height;

  const subtitle =
    emptyMessage &&
    emptyMessage !== "No data found" &&
    emptyMessage !== "No records found"
      ? emptyMessage
      : "There are no records to display";

  const emptyTemplate = `
    <div class="py-8 px-4 flex flex-col items-center justify-center text-center select-none">
      <div class="w-14 h-14 rounded-2xl bg-slate-50 border border-slate-200/80 flex items-center justify-center mb-2.5 text-slate-400 shadow-2xs">
        <svg class="w-7 h-7 text-slate-400 stroke-[1.6]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" d="M2.25 13.5h3.86a2.25 2.25 0 0 1 2.012 1.244l.256.512a2.25 2.25 0 0 0 2.013 1.244h3.218a2.25 2.25 0 0 0 2.013-1.244l.256-.512a2.25 2.25 0 0 1 2.013-1.244h3.859m-19.5.375v4.5a2.25 2.25 0 0 0 2.25 2.25h15a2.25 2.25 0 0 0 2.25-2.25v-4.5m-19.5 0L4.5 4.5h15l2.25 9.375" />
        </svg>
      </div>
      <span class="text-sm font-bold text-slate-700 leading-tight">No data found</span>
      <span class="text-xs text-slate-400 mt-1 font-normal">${subtitle}</span>
    </div>
  `;

  const loadingTemplate = `
    <div class="py-10 flex items-center justify-center text-[#006747] font-medium gap-2.5">
      <svg class="animate-spin w-5 h-5 text-[#006747]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
      </svg>
      <span class="text-sm font-semibold text-slate-700">Loading data...</span>
    </div>
  `;

  return (
    <div
      className={cn(
        "ag-theme-quartz w-full",
        isEmpty && "ag-empty-grid",
        className
      )}
      style={{
        height: effectiveHeight,
        minHeight: isPlaceholder ? "280px" : undefined,
        width: "100%",
      }}
    >
      <AgGridReact
        rowData={rowData}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        loading={loading}
        pagination={pagination}
        paginationPageSize={paginationPageSize}
        domLayout={effectiveDomLayout}
        rowHeight={rowHeight}
        headerHeight={headerHeight}
        animateRows={true}
        suppressCellFocus={true}
        suppressMovableColumns={true}
        onGridReady={handleGridReady}
        quickFilterText={quickFilterText}
        overlayNoRowsTemplate={emptyTemplate}
        overlayLoadingTemplate={loadingTemplate}
      />
    </div>
  );
};

export default AgGridTable;
